import { useState, useEffect, useRef } from 'react';
import { emailListAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Upload, Mail, Trash2, Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailListTab() {
  const [emailList, setEmailList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, email: null });
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    notes: ''
  });
  
  const fileInputRef = useRef(null);
  const itemsPerPage = 10;

  useEffect(() => {
    loadEmailList();
    loadCount();
  }, [currentPage, searchQuery]);

  const loadEmailList = async () => {
    try {
      setLoading(true);
      const params = {
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage
      };
      if (searchQuery) {
        params.search = searchQuery;
      }
      
      const res = await emailListAPI.getAll(params);
      setEmailList(res.data);
    } catch (error) {
      toast.error('Gagal memuat data email');
    } finally {
      setLoading(false);
    }
  };

  const loadCount = async () => {
    try {
      const res = await emailListAPI.getCount();
      setTotalCount(res.data.total);
    } catch (error) {
      console.error('Error loading count:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadEmailList();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email) {
      toast.error('Email wajib diisi');
      return;
    }

    try {
      await emailListAPI.add(formData);
      toast.success('Email berhasil ditambahkan');
      setDialogOpen(false);
      resetForm();
      loadEmailList();
      loadCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambah email');
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files[0];
    
    if (!file) {
      toast.error('Pilih file CSV terlebih dahulu');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await emailListAPI.import(formData);
      toast.success(res.data.message);
      setImportDialogOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      loadEmailList();
      loadCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal import email');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.email) return;
    
    try {
      await emailListAPI.delete(deleteDialog.email.id);
      toast.success('Email berhasil dihapus');
      setDeleteDialog({ open: false, email: null });
      loadEmailList();
      loadCount();
    } catch (error) {
      toast.error('Gagal menghapus email');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      notes: ''
    });
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl font-bold text-slate-900">Upload Email</h1>
        <div className="flex gap-2">
          <Button onClick={() => setImportDialogOpen(true)} variant="outline" className="border-[#4472C4] text-[#4472C4]">
            <Upload className="w-4 h-4 mr-2" />
            Import Data
          </Button>
        </div>
      </div>

      {/* Stats & Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="text-lg font-semibold">
              Total Email Terdaftar: <span className="text-[#4472C4]">{totalCount}</span>
            </div>
            
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input
                placeholder="Cari email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-64"
              />
              <Button type="submit" className="bg-[#4472C4] hover:bg-[#3461B3]">
                Cari Email
              </Button>
            </form>
          </div>
        </CardContent>
      </Card>

      {/* Email List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-[#4472C4] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="mt-2 text-slate-500">Memuat...</p>
            </div>
          ) : emailList.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Belum ada email terdaftar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">No</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Nama</th>
                    <th className="px-4 py-3 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {emailList.map((email, idx) => (
                    <tr key={email.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3">{(currentPage - 1) * itemsPerPage + idx + 1}</td>
                      <td className="px-4 py-3 font-medium">{email.email}</td>
                      <td className="px-4 py-3">{email.name || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteDialog({ open: true, email })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <span className="px-4 text-sm">
                {currentPage} / {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Import Data Email</DialogTitle>
            <DialogDescription>
              Upload file CSV dengan kolom: email, name (opsional)
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleImport} className="space-y-4">
            <div>
              <Label>File CSV</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
              />
              <p className="text-xs text-slate-500 mt-1">Format: CSV dengan header "email" dan "name"</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setImportDialogOpen(false)}>
                Batal
              </Button>
              <Button type="submit" className="bg-[#4472C4] hover:bg-[#3461B3]">
                Import
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, email: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Email?</AlertDialogTitle>
            <AlertDialogDescription>
              Apakah Anda yakin ingin menghapus email "{deleteDialog.email?.email}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
